// ChartTab — ProChart with real-time WebSocket ticks, indicators & algo overlays
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTrading } from '../../../context/TradingContext';
import { useCandleData } from '../../../hooks/useMarketData';
import ProChart from '../chart/ProChart';
import IndicatorPanel from '../chart/IndicatorPanel';
import { TIMEFRAMES } from '../../../config/tradingConstants';
import wsManager from '../../../services/angelOneWebSocket';
import { FaCog, FaSync, FaLayerGroup } from 'react-icons/fa';

const CHART_SYMBOLS = [
  { name: 'NIFTY 50', token: '99926000', exchange: 'NSE' },
  { name: 'BANK NIFTY', token: '99926009', exchange: 'NSE' },
  { name: 'SENSEX', token: '99919000', exchange: 'BSE' },
  { name: 'NIFTY IT', token: '99926013', exchange: 'NSE' },
  { name: 'NIFTY FIN', token: '99926037', exchange: 'NSE' },
];

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d} ${h}:${min}`;
}

export default function ChartTab() {
  const { connectionStatus, algoOverlay, subscribeTicks, unsubscribeTicks } = useTrading();
  const [symbolIdx, setSymbolIdx] = useState(0);
  const symbol = CHART_SYMBOLS[symbolIdx];
  const [timeframe, setTimeframe] = useState('FIFTEEN_MINUTE');
  const [activeIndicators, setActiveIndicators] = useState(['sma']);
  const [showIndicatorPanel, setShowIndicatorPanel] = useState(false);
  const [showOverlays, setShowOverlays] = useState(true);
  const [liveTick, setLiveTick] = useState(null);
  const prevTokenRef = useRef(null);

  const tf = TIMEFRAMES.find(t => t.key === timeframe) || TIMEFRAMES[3];

  const toDate = useMemo(() => formatDate(new Date()), []);
  const fromDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - tf.days);
    return formatDate(d);
  }, [tf.days]);

  const { candles, loading, refetch } = useCandleData(symbol.exchange, symbol.token, timeframe, fromDate, toDate);

  // Subscribe WebSocket ticks for current symbol (QUOTE mode = 2 for OHLCV)
  useEffect(() => {
    if (connectionStatus !== 'connected') return;

    const tokenObj = { exchange: symbol.exchange, token: symbol.token };

    // Unsubscribe previous symbol
    if (prevTokenRef.current && prevTokenRef.current.token !== symbol.token) {
      unsubscribeTicks([prevTokenRef.current]);
    }
    prevTokenRef.current = tokenObj;

    // Subscribe with QUOTE mode (2) for OHLCV data
    subscribeTicks([tokenObj], 2);

    // Listen for ticks matching our symbol
    const handleTick = (tick) => {
      if (tick.token === symbol.token) {
        setLiveTick(tick);
      }
    };
    const unsub = wsManager.on('tick', handleTick);

    return () => {
      unsub();
    };
  }, [connectionStatus, symbol.exchange, symbol.token, subscribeTicks, unsubscribeTicks]);

  // Reset liveTick on symbol/timeframe change
  const handleSymbolChange = useCallback((idx) => {
    setSymbolIdx(idx);
    setLiveTick(null);
  }, []);

  const handleTimeframeChange = useCallback((tf) => {
    setTimeframe(tf);
    setLiveTick(null);
  }, []);

  // Derive overlay props from algoOverlay context
  const overlaySignal = showOverlays ? algoOverlay?.signal : null;
  const overlayExpectedMove = showOverlays ? algoOverlay?.expectedMove : null;
  const overlayRegime = showOverlays ? algoOverlay?.regime : null;

  if (connectionStatus !== 'connected') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
        <p className="text-sm text-gray-400">Connect to Angel One to view charts</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Chart Controls */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          {/* Symbol Selector */}
          <div className="flex items-center gap-1">
            {CHART_SYMBOLS.map((s, i) => (
              <button
                key={s.token}
                onClick={() => handleSymbolChange(i)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all ${
                  symbolIdx === i
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>

          {/* Timeframe Selector */}
          <div className="flex items-center gap-0.5 bg-gray-50 rounded-lg p-0.5 border border-gray-200">
            {TIMEFRAMES.map(t => (
              <button
                key={t.key}
                onClick={() => handleTimeframeChange(t.key)}
                className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${
                  timeframe === t.key
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowOverlays(!showOverlays)}
            className={`p-2 rounded-lg text-xs transition-all ${
              showOverlays
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-400 hover:text-gray-700 hover:bg-gray-50'
            }`}
            title={showOverlays ? 'Hide algo overlays' : 'Show algo overlays'}
          >
            <FaLayerGroup />
          </button>
          <button
            onClick={() => refetch()}
            className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-gray-50 text-xs"
            title="Refresh"
          >
            <FaSync className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowIndicatorPanel(!showIndicatorPanel)}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50 text-xs"
          >
            <FaCog />
          </button>
        </div>
      </div>

      {/* Indicator Panel */}
      {showIndicatorPanel && (
        <IndicatorPanel
          activeIndicators={activeIndicators}
          onToggle={(key) => {
            setActiveIndicators(prev =>
              prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
            );
          }}
        />
      )}

      {/* Chart */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" style={{ height: '560px' }}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : candles.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <p className="text-sm text-gray-400">No chart data available</p>
            <p className="text-[10px] text-gray-300">Market may be closed. Try selecting a longer timeframe (1D).</p>
            <button onClick={() => refetch()} className="text-xs text-blue-600 hover:underline">Retry</button>
          </div>
        ) : (
          <ProChart
            candles={candles}
            indicators={activeIndicators}
            symbolName={symbol.name}
            timeframe={timeframe}
            liveTick={liveTick}
            signal={overlaySignal}
            expectedMove={overlayExpectedMove}
            regime={overlayRegime}
          />
        )}
      </div>
    </div>
  );
}
